/**
 *  @class
 *  @function CountdownTimer
 *
 *  Counts down to data-target, an ISO 8601 date and time with its UTC offset
 *  (2027-03-01T00:00:00+03:00). data-expired says what happens at the end:
 *  "hide" hides the section (never in the theme editor, which shows a note
 *  instead), "message" swaps the digits for .countdown-timer--message and
 *  "zero", the default, keeps 00s (the overlay popup's countdown block).
 *  "remove" removes the message the timer sits in ([data-countdown-item], an
 *  announcement bar message; through Flickity when it is a carousel slide)
 *  and tells its container with a bubbling countdown:removed event. The
 *  editor keeps the message and shows its [data-countdown-notice] instead.
 *
 *  data-format="compact" writes the time left as one line of text in
 *  .countdown-timer--compact ("2d 04h 12m 09s", the days only while there
 *  are any), with Intl unit names in the page's language, so nothing needs
 *  translating, and no digit animation. With "message", the message then
 *  takes the place of everything else in the timer. The announcement bar
 *  writes the first value before countdown-banner.js arrives, the same way
 *  (sections/announcement-bar.liquid): keep the two in step.
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.compact = this.dataset.format === 'compact' ? this.querySelector('.countdown-timer--compact') : null;
      this.locale = document.documentElement.lang || undefined;
      if (!this.compact) CountdownTimer.injectBlurFilter();

      this.numbers = ['days', 'hours', 'minutes', 'seconds'].map((unit) => this.querySelector(`.${unit} .countdown-timer--column--number`));
      this.behavior = this.dataset.expired || 'zero';
      this.target = CountdownTimer.parseTarget(this.dataset.target);
      this.ended = false;

      // Build per-digit clip containers for each number element.
      this.numbers.forEach((el) => {
        if (el) el.textContent = '';
      });

      // A date that can't be read shows zeros and stops there.
      if (Number.isNaN(this.target)) {
        this.render(0);
        return;
      }

      // Over already (eg. a page cached before the end): act now, before the
      // timer comes anywhere near the viewport.
      if (this.target <= Date.now()) {
        this.end();
        return;
      }

      // Draw the time left straight away, so the digits never sit empty,
      // then tick once the timer is near the viewport. The end comes on
      // time either way: a section that hides goes while still out of
      // view, not as the shopper scrolls to it.
      this.render(this.target - Date.now());
      this.scheduleEnd();
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.io.disconnect();
          this.io = null;
          this.tick();
        }
      }, { rootMargin: '0px 0px 500px 0px' });
      this.io.observe(this);
    }
    disconnectedCallback() {
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      clearTimeout(this.timer);
      this.timer = null;
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
    // A timeout can wait about 24.8 days at most: a later end is checked
    // again then.
    scheduleEnd() {
      const left = this.target - Date.now();
      if (left <= 0) {
        this.end();
        return;
      }
      this.endTimer = setTimeout(() => this.scheduleEnd(), Math.min(left, 2147483647));
    }
    tick() {
      const distance = this.target - Date.now();

      if (distance <= 0) {
        this.timer = null;
        this.end();
        return;
      }

      this.render(distance);

      // Wake just after the seconds digit changes, rather than every frame.
      this.timer = setTimeout(() => this.tick(), (distance % 1000) + 20);
    }
    render(distance) {
      if (this.compact) {
        CountdownTimer.writeCompact(this.compact, CountdownTimer.formatCompact(distance, this.locale));
        return;
      }

      const second = 1000,
        minute = second * 60,
        hour = minute * 60,
        day = hour * 24,
        values = distance > 0 ? [
          Math.floor(distance / day),
          Math.floor((distance % day) / hour),
          Math.floor((distance % hour) / minute),
          Math.floor((distance % minute) / second)
        ] : [0, 0, 0, 0];

      this.numbers.forEach((el, i) => {
        if (el) CountdownTimer.setNumber(el, CountdownTimer.addZero(values[i]));
      });
    }
    end() {
      if (this.ended) return;
      this.ended = true;
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      clearTimeout(this.timer);
      this.timer = null;
      clearTimeout(this.endTimer);
      this.endTimer = null;
      this.render(0);

      if (this.behavior === 'message') {
        const message = this.querySelector('.countdown-timer--message');
        if (message) {
          this.classList.add('countdown-timer--ended');
          message.hidden = false;
          if (this.compact) {
            Array.from(this.children).forEach((child) => {
              child.hidden = child !== message;
            });
          }
        }
        return;
      }

      if (this.behavior === 'remove') {
        this.removeItem();
        return;
      }

      if (this.behavior !== 'hide') return;

      // The editor keeps the section, and says shoppers no longer see it.
      if (window.Shopify?.designMode) {
        const notice = this.closest('.countdown-banner')?.querySelector('[data-countdown-notice]');
        if (notice) notice.hidden = false;
        return;
      }

      const section = this.closest('.shopify-section');
      if (section) section.hidden = true;
    }
    removeItem() {
      const item = this.closest('[data-countdown-item]');
      if (!item) return;

      if (window.Shopify?.designMode) {
        const notice = item.querySelector('[data-countdown-notice]');
        if (notice) notice.hidden = false;
        return;
      }

      const container = item.parentElement,
        slideshow = item.closest('slide-show'),
        flkty = slideshow && slideshow._flkty;
      if (flkty && flkty.getCellElements().includes(item)) {
        flkty.remove(item);
      } else {
        item.remove();
      }
      (slideshow || container).dispatchEvent(new CustomEvent('countdown:removed', { bubbles: true }));
    }
    // Whole seconds, rounded down as the digits are: "2d 04h 12m 09s",
    // "04h 12m 09s". Without Intl unit support, "2d 04h 12m 09s" in Latin
    // letters.
    static formatCompact(distance, locale) {
      const total = Math.max(0, Math.floor(distance / 1000)),
        parts = [
          [Math.floor(total / 86400), 'day', 1],
          [Math.floor((total % 86400) / 3600), 'hour', 2],
          [Math.floor((total % 3600) / 60), 'minute', 2],
          [total % 60, 'second', 2]
        ];
      return parts
        .filter((part) => part[1] !== 'day' || part[0] > 0)
        .map(([value, unit, digits]) => {
          try {
            return new Intl.NumberFormat(locale, { style: 'unit', unit: unit, unitDisplay: 'narrow', minimumIntegerDigits: digits }).format(value);
          } catch (e) {
            return String(value).padStart(digits, '0') + unit.charAt(0);
          }
        })
        .join(' ');
    }
    // The time, and under it the same time with every digit a 0 (hidden),
    // which sets the width: 0 is as wide as any digit in most fonts, so the
    // time keeps one width as it ticks, even in a font without tabular
    // figures, and the line around it never moves.
    static writeCompact(el, text) {
      if (el.children.length < 2) {
        const shadow = document.createElement('span');
        shadow.setAttribute('aria-hidden', 'true');
        el.replaceChildren(document.createElement('span'), shadow);
      }
      el.children[0].textContent = text;
      el.children[1].textContent = text.replace(/\d/g, '0');
    }
    static parseTarget(iso) {
      // Only this exact ISO 8601 shape is read the same way by every browser,
      // Safari included.
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/.test(iso || '')) {
        return NaN;
      }
      return new Date(iso).getTime();
    }
    static injectBlurFilter() {
      // Inject vertical-only Gaussian blur SVG filter once
      if (document.getElementById('countdown-blur-y')) return;

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.style.position = 'absolute';
      svg.innerHTML = '<filter id="countdown-blur-y"><feGaussianBlur in="SourceGraphic" stdDeviation="0 3"/></filter>';
      document.body.appendChild(svg);
    }
    static setNumber(el, newVal) {
      const digits = String(newVal).split('');
      let clips = el.querySelectorAll('.countdown-digit');

      // Add or remove digit containers when digit count changes
      while (clips.length < digits.length) {
        const d = CountdownTimer.createDigit('');
        el.appendChild(d);
        clips = el.querySelectorAll('.countdown-digit');
      }
      while (clips.length > digits.length) {
        el.removeChild(clips[clips.length - 1]);
        clips = el.querySelectorAll('.countdown-digit');
      }

      // Update each digit independently — only animate changed ones
      digits.forEach((digit, i) => {
        const clip = clips[i];
        const current = clip.querySelector('.countdown-num--current');
        const entering = clip.querySelector('.countdown-num--entering');

        if ((current && current.textContent === digit) ||
            (entering && entering.textContent === digit)) return;

        clip.querySelectorAll('.countdown-num--leaving').forEach(s => s.remove());
        if (entering) entering.remove();

        if (current) {
          current.className = 'countdown-num--leaving';
          current.addEventListener('animationend', () => current.remove(), { once: true });
          setTimeout(() => { if (current.parentNode) current.remove(); }, 500);
        }

        const sizer = clip.querySelector('.countdown-sizer');
        if (sizer) sizer.textContent = digit;

        const next = document.createElement('span');
        next.className = 'countdown-num--entering';
        next.textContent = digit;
        clip.appendChild(next);

        const promote = () => { next.className = 'countdown-num--current'; };
        next.addEventListener('animationend', promote, { once: true });
        setTimeout(() => {
          if (next.classList.contains('countdown-num--entering')) promote();
        }, 500);
      });
    }
    static createDigit(val) {
      const d = document.createElement('span');
      d.className = 'countdown-digit';
      const sizer = document.createElement('span');
      sizer.className = 'countdown-sizer';
      sizer.textContent = val;
      d.appendChild(sizer);
      return d;
    }
    static addZero(x) {
      return (x < 10 && x >= 0) ? "0" + x : x;
    }
  }
  customElements.define('countdown-timer', CountdownTimer);
}
